/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, Polyline, Marker, InfoWindow } from "@react-google-maps/api";

import PickupMarker from "../../../../assets/icons/pickup.svg";
import TruckMarker from "../../../../assets/icons/truck-marker.svg";
import WaitingPointMarker from "../../../../assets/icons/WaitingPointMarker.svg";
import LowBatteryMarker from "../../../../assets/icons/LowBatteryMarker.svg";
import DeadBatteryMarker from "../../../../assets/icons/DeadBatteryMarker.svg";
import DropoffMarker from "../../../../components/GoogleMaps/DropoffMarker";
import EndPoint from "../../../../assets/icons/end-point.svg";
import liveLocationIcon from "../../../../assets/images/liveLocation.png";
import activeliveLocationIcon from "../../../../assets/images/activeliveLocation.png";
import PointDetails from "../../../../components/GoogleMaps/PointDetails";
import { svgToDataURL } from "../../../../utils/svgToUrl";
import trackAnalytics from "trackAnalytics";
import useGoogleMapsLoader from "hooks/useGoogleMapsLoader";
import LifeTrackingWindowInfo from "modules/LiveTracking/components/LifeTrackingWindowInfo";

const calculateBounds = (coordinates) => {
    return coordinates.reduce(
        (bounds, coord) => {
            bounds.minLat = Math.min(bounds.minLat, coord.lat);
            bounds.maxLat = Math.max(bounds.maxLat, coord.lat);
            bounds.minLng = Math.min(bounds.minLng, coord.lng);
            bounds.maxLng = Math.max(bounds.maxLng, coord.lng);
            return bounds;
        },
        {
            minLat: coordinates[0].lat,
            maxLat: coordinates[0].lat,
            minLng: coordinates[0].lng,
            maxLng: coordinates[0].lng,
        }
    );
};

const egyptBounds = {
    southWest: { lat: 22.0, lng: 25.0 },
    northEast: { lat: 31.0, lng: 35.0 },
};

const GoogleMapRoute = ({
    tripData,
    activeMarker,
    handleActiveMarker,
    tripStatus,
    trip,
    containerElement,
    liveTrackingTrips,
    selectedTrip,
    mapCenter,
    mapZoom,
    setSelectedTrip,
    allActiveTrips,
    setMapView,
}) => {
    const mapRef = useRef(null);

    const { isLoaded } = useGoogleMapsLoader();

    const onLoad = React.useCallback(function callback(map) {
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(
            new window.google.maps.LatLng(egyptBounds.southWest.lat, egyptBounds.southWest.lng)
        );
        bounds.extend(
            new window.google.maps.LatLng(egyptBounds.northEast.lat, egyptBounds.northEast.lng)
        );
        map.fitBounds(bounds);
        mapRef.current = map;
    }, []);

    useEffect(() => {
        if (mapRef.current && tripData?.coordinates?.length > 0) {
            const { minLat, maxLat, minLng, maxLng } = calculateBounds(tripData?.coordinates);
            const bounds = new window.google.maps.LatLngBounds();
            bounds.extend(new window.google.maps.LatLng(minLat, minLng));
            bounds.extend(new window.google.maps.LatLng(maxLat, maxLng));
            mapRef.current.fitBounds(bounds);
        }
    }, [tripData?.coordinates?.length]);

    const lastPoint = useMemo(() => {
        if (tripData?.coordinates?.length > 0) {
            return tripData?.coordinates[0];
        }
    }, [tripData?.coordinates?.length]);

    const mixpanelData = {
        "Trip Source": trip?.source,
        "Trip Status": trip?.status,
        "Trip Id": trip?.id,
        "Geofencing Status": trip?.geofence,
    };

    const renderInfoWindow = (pointType, point) => (
        <InfoWindow onCloseClick={() => handleActiveMarker(null)}>
            <PointDetails
                pointType={pointType}
                point={point}
            />
        </InfoWindow>
    );

    const handleActiveMarkerClick = (point, pointType) => {
        switch (pointType) {
            case "pickup":
                trackAnalytics("Route Pickup point clicks", { ...mixpanelData });
                break;
            case "waitingPoint":
                trackAnalytics("Route waiting point clicks", {
                    ...mixpanelData,
                    "Waiting Time Hours": point.waitingTime,
                });
                break;
            case "lowBatteryPoint":
                trackAnalytics("Route low battery warning clicks", {
                    ...mixpanelData,
                    "Battery Percentage": `${point.batteryLevel}%`,
                });
                break;
            case "dropoff":
                trackAnalytics("Route drop-off point clicks", { ...mixpanelData });
                break;
            default:
                break;
        }
        handleActiveMarker(point);
    };

    const renderMarkers = (point, iconUrl, pointType) => (
        <Marker
            position={point}
            options={{
                icon: {
                    url: iconUrl,
                },
            }}
            onClick={() => handleActiveMarkerClick(point, pointType)}
            zIndex={pointType === "pickup" ? 1000 : 0}
        >
            {activeMarker?.lat === point?.lat &&
                activeMarker?.lng === point?.lng &&
                renderInfoWindow(pointType, point)}
        </Marker>
    );

    const MARKER_ICON_SIZE = { width: 55, height: 55 }; // Match your icon's true dimensions
    const MARKER_ANCHOR = { x: MARKER_ICON_SIZE.width / 2, y: MARKER_ICON_SIZE.height }; // Bottom-center

    const ICON_CONFIG = (url) => ({
        url,
        scaledSize: MARKER_ICON_SIZE,
        anchor: new window.google.maps.Point(MARKER_ANCHOR.x, MARKER_ANCHOR.y),
    });

    const renderLiveTrackingMarkers = () =>
        liveTrackingTrips?.map((singleTrip) => {
            const lat = Number(singleTrip?.loc?.lat);
            const lng = Number(singleTrip?.loc?.lon);

            if (isNaN(lat) || isNaN(lng)) return null;

            const isSelected = singleTrip.tid === selectedTrip?.tid;
            const activeTripDetails = allActiveTrips.find(
                (activeTrip) => activeTrip.id === singleTrip.tid
            );
            return (
                <Marker
                    position={{ lat, lng }}
                    options={{
                        icon: ICON_CONFIG(isSelected ? activeliveLocationIcon : liveLocationIcon),
                        optimized: false,
                    }}
                    onClick={() => {
                        setSelectedTrip((prev) =>
                            prev?.tid === singleTrip.tid ? null : singleTrip
                        );
                        trackAnalytics("truck_focus_live_location", {
                            selectedTripId: singleTrip.tid,
                        });
                    }}
                    key={singleTrip.id}
                >
                    {isSelected && (
                        <InfoWindow
                            visible={isSelected}
                            onCloseClick={() => setSelectedTrip(null)}
                        >
                            <LifeTrackingWindowInfo
                                selectedTripDetails={{
                                    ...selectedTrip,
                                    activeTripDetails,
                                }}
                            />
                        </InfoWindow>
                    )}
                </Marker>
            );
        });

    const renderPickupPoint = () => {
        if (tripData?.pickupPoint) {
            return renderMarkers(tripData?.pickupPoint, PickupMarker, "pickup");
        }
        return null;
    };

    const renderDropoffPoints = () => {
        if (tripData?.dropoffs.length > 0) {
            return tripData?.dropoffs.map((dropOff, index) => {
                const isLastPoint =
                    tripStatus === "completed" && index === tripData.dropoffs.length - 1;

                return renderMarkers(
                    dropOff,
                    isLastPoint ? EndPoint : svgToDataURL(<DropoffMarker index={dropOff.index} />),
                    isLastPoint ? "endPoint" : "dropoff"
                );
            });
        }
        return null;
    };

    const renderWaitingPoints = () => {
        if (tripData?.waitingPoints?.length > 0) {
            return tripData?.waitingPoints.map((waitingPoint) =>
                renderMarkers(waitingPoint, WaitingPointMarker, "waitingPoint")
            );
        }
        return null;
    };

    const renderLowBatteryPoints = () => {
        if (tripData?.lowBatteryPoints?.length > 0) {
            return tripData?.lowBatteryPoints.map((lowBatteryPoint) =>
                renderMarkers(lowBatteryPoint, LowBatteryMarker, "lowBatteryPoint")
            );
        }
        return null;
    };

    const renderDeadBatteryPoints = () => {
        if (tripData?.deadBatteryPoints?.length > 0) {
            return tripData?.deadBatteryPoints.map((deadBatteryPoint) =>
                renderMarkers(deadBatteryPoint, DeadBatteryMarker, "deadBatteryPoint")
            );
        }
        return null;
    };

    useEffect(() => {
        if (!selectedTrip) return;

        const latestTripData = liveTrackingTrips?.find((trip) => trip.tid === selectedTrip.tid);

        if (latestTripData && latestTripData.loc) {
            setSelectedTrip((prev) => ({
                ...prev,
                loc: latestTripData.loc,
            }));
        }
    }, [liveTrackingTrips]);

    useEffect(() => {
        if (selectedTrip?.loc && mapRef.current) {
            const { lat, lon } = selectedTrip.loc;
            const parsedLat = Number(lat);
            const parsedLng = Number(lon);

            if (!isNaN(parsedLat) && !isNaN(parsedLng)) {
                setMapView({
                    center: { lat: parsedLat, lng: parsedLng },
                });

                const newPosition = new window.google.maps.LatLng(parsedLat, parsedLng);
                mapRef.current.panTo(newPosition);
                mapRef.current.setZoom(15);
            }
        }
    }, [selectedTrip]);
    //     if (mapRef.current !== null) {
    //         const zoomLevel = mapRef.current.getZoom();
    //         let newScale = 0.7;
    //         if (zoomLevel <= 14) {
    //             newScale = 0;
    //         } else if (zoomLevel <= 17) {
    //             setNumOfPointsToSkip(10);
    //         } else if (zoomLevel <= 19) {
    //             setNumOfPointsToSkip(2);
    //         } else {
    //             setNumOfPointsToSkip(1);
    //         }
    //         setArrowSymbolScale(newScale);
    //     }
    // };
    return (
        <React.Fragment>
            {isLoaded && (
                <GoogleMap
                    ref={mapRef}
                    mapTypeId="terrain"
                    onZoomChanged={100}
                    onLoad={onLoad}
                    mapContainerStyle={containerElement}
                    center={mapCenter}
                    zoom={mapZoom}
                >
                    {renderPickupPoint()}
                    {renderDropoffPoints()}
                    {renderWaitingPoints()}
                    {renderLowBatteryPoints()}
                    {renderDeadBatteryPoints()}
                    {renderDeadBatteryPoints()}
                    {renderLiveTrackingMarkers()}
                    {tripStatus === "moving" && (
                        <Marker
                            position={lastPoint}
                            options={{
                                icon: {
                                    url: TruckMarker,
                                },
                            }}
                        />
                    )}
                    {tripData?.coordinates?.length > 0 && (
                        <Polyline
                            path={tripData.coordinates}
                            geodesic
                            options={{
                                strokeColor: "black",
                                strokeOpacity: 0.5,
                                strokeWeight: 5,
                                icons: [
                                    {
                                        icon: {
                                            path: "M7 17L12 22L17 17L15.6 15.6L13 18.175L13 2H11L11 18.175L8.4 15.6L7 17Z",
                                            fillColor: "white",
                                            fillOpacity: 1,
                                            strokeWeight: 0,
                                            rotation: -1.6984654673861355,
                                            scale: 0.7,
                                            anchor: {
                                                x: 12,
                                                y: 12,
                                            },
                                        },
                                        offset: "20%",
                                        repeat: "100px",
                                    },
                                ],
                            }}
                        />
                    )}
                </GoogleMap>
            )}
        </React.Fragment>
    );
};

export default GoogleMapRoute;
